import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { getSupplierEmailIgnores, type SupplierEmailIgnoreDto } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'

interface SupplierIgnoresDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** « Restaurer un ignoré ouvre une création préremplie ». */
  onRestore: (ignore: SupplierEmailIgnoreDto) => void
}

/**
 * SOLY-SUPPLIER-002 MS-03 — « Mes ignorés »: the domains this person left unticked. Never proposed to
 * them again; a colleague's ignores are not here, and do not hide anything from them.
 */
export function SupplierIgnoresDialog({ open, onOpenChange, onRestore }: SupplierIgnoresDialogProps) {
  const [ignores, setIgnores] = useState<SupplierEmailIgnoreDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let active = true
    getSupplierEmailIgnores()
      .then((list) => {
        if (active) {
          setIgnores(list)
          setError(null)
        }
      })
      .catch((e) => {
        if (active) setError(e instanceof ApiError ? e.message : 'Vos ignorés n’ont pas pu être chargés.')
      })
    return () => {
      active = false
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="supplier-ignores-dialog">
        <DialogHeader>
          <DialogTitle>Mes ignorés</DialogTitle>
          <DialogDescription>Les sociétés que vous avez décochées : elles ne vous sont plus proposées.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {ignores === null && !error && <Skeleton className="h-24 w-full" />}
        {ignores !== null && ignores.length === 0 && <p className="text-sm text-muted-foreground">Aucun ignoré.</p>}
        {ignores !== null && ignores.length > 0 && (
          <ul className="divide-y rounded-md border">
            {ignores.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid="supplier-ignores-row">
                <span className="break-all">{i.domain}</span>
                <Button size="sm" variant="outline" onClick={() => onRestore(i)} data-testid="supplier-ignores-restore-btn">
                  Ajouter
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
